import React, { useEffect, useState } from 'react';

const STEPS = [
  { short: 'Verify', label: 'Verifying Google address…' },
  { short: 'Towers', label: 'Scanning nearest Airmax towers…' },
  { short: 'LOS', label: 'Checking line of sight…' },
  { short: 'Done', label: 'Confirming coverage…' },
];

type Props = {
  active: boolean;
  label?: string;
  /** @deprecated kept for callers — both surfaces use the same animation now */
  compact?: boolean;
};

/** Shared coverage-check animation for homepage + plans modal. */
const CoverageCheckAnimation: React.FC<Props> = ({ active, label }) => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!active) {
      setStep(0);
      return;
    }
    setStep(0);
    const timers = [
      window.setTimeout(() => setStep(1), 650),
      window.setTimeout(() => setStep(2), 1400),
      window.setTimeout(() => setStep(3), 2200),
    ];
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [active]);

  if (!active) return null;

  return (
    <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-b from-slate-50 to-white shadow-sm">
      <div className="relative px-5 pt-7 pb-6 text-center">
        {/* soft glow */}
        <div
          className="pointer-events-none absolute inset-0 opacity-70"
          style={{
            background:
              'radial-gradient(ellipse 60% 50% at 50% 35%, rgba(37,99,235,0.12), transparent 70%)',
          }}
        />

        {/* Radar */}
        <div className="relative mx-auto h-28 w-28">
          <span className="absolute inset-0 rounded-full border border-blue-200/80 animate-ping opacity-40" style={{ animationDuration: '2.2s' }} />
          <span className="absolute inset-3 rounded-full border border-blue-300/60 animate-ping opacity-50" style={{ animationDuration: '2.2s', animationDelay: '0.35s' }} />
          <span className="absolute inset-6 rounded-full border-2 border-dashed border-blue-400/50 animate-[spin_8s_linear_infinite]" />
          <span className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-50 to-sky-100 shadow-inner" />

          {/* sweeping arc */}
          <svg className="absolute inset-0 h-full w-full animate-[spin_2.4s_linear_infinite]" viewBox="0 0 112 112" aria-hidden>
            <defs>
              <linearGradient id="amxSweep" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="rgba(37,99,235,0)" />
                <stop offset="100%" stopColor="rgba(37,99,235,0.55)" />
              </linearGradient>
            </defs>
            <path d="M56 56 L56 10 A46 46 0 0 1 98 56 Z" fill="url(#amxSweep)" />
          </svg>

          {/* tower mark */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-airmax-blue text-white shadow-lg shadow-blue-500/30">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M12 22V10" />
                <path d="M5 22h14" />
                <path d="M8 10l4-6 4 6" />
                <path d="M9.5 14a4.5 4.5 0 0 1 5 0" />
                <path d="M7.5 17a8 8 0 0 1 9 0" />
              </svg>
            </div>
          </div>
        </div>

        <p className="relative mt-5 text-base font-bold tracking-tight text-slate-900">
          {label || 'Checking coverage…'}
        </p>
        <p
          key={step}
          className="relative mt-1.5 text-sm text-slate-600 animate-[fadeIn_0.35s_ease-out]"
        >
          {STEPS[step]?.label}
        </p>

        {/* step rail */}
        <div className="relative mx-auto mt-6 flex max-w-xs items-start justify-between px-1">
          {STEPS.map((s, i) => {
            const done = i < step;
            const current = i === step;
            return (
              <div key={s.short} className="relative z-10 flex w-14 flex-col items-center gap-1.5">
                <span
                  className={`flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-bold transition-all duration-300 ${
                    done
                      ? 'bg-emerald-500 text-white scale-100'
                      : current
                        ? 'bg-airmax-blue text-white scale-110 shadow-md shadow-blue-500/40'
                        : 'bg-slate-200 text-slate-400'
                  }`}
                >
                  {done ? (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    i + 1
                  )}
                </span>
                <span
                  className={`text-[10px] font-semibold uppercase tracking-wide ${
                    current ? 'text-airmax-blue' : done ? 'text-emerald-600' : 'text-slate-400'
                  }`}
                >
                  {s.short}
                </span>
              </div>
            );
          })}
          {/* connector line behind dots */}
          <div className="pointer-events-none absolute left-8 right-8 top-3.5 h-0.5 bg-slate-200">
            <div
              className="h-full bg-airmax-blue transition-all duration-500 ease-out"
              style={{ width: `${(step / (STEPS.length - 1)) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export async function withMinCheckDuration<T>(promise: Promise<T>, minMs = 2600): Promise<T> {
  const started = Date.now();
  const result = await promise;
  const wait = minMs - (Date.now() - started);
  if (wait > 0) await new Promise((r) => setTimeout(r, wait));
  return result;
}

export default CoverageCheckAnimation;
